import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import clsx from 'clsx';

import { columnToken, type ColumnPreset } from '../tokens';
import { SortAscIcon, SortDescIcon, SortIcon } from '../internal/icons';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Table.module.css';

export type SortDirection = 'asc' | 'desc' | 'none';

const TableContext = createContext<{ dense: boolean }>({ dense: false });

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /**
   * The table's name. Kept in the accessibility tree even when hidden — a data
   * grid with no caption is a wall of cells to a screen-reader user.
   */
  caption: ReactNode;
  hideCaption?: boolean;
  /** 32px rows instead of 40px: the catalogue and validation tables. */
  dense?: boolean;
  /** Header stays put while the body scrolls. Needs a scroll container. */
  stickyHead?: boolean;
  children: ReactNode;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { caption, hideCaption = true, dense = false, stickyHead = false, className, children, ...rest },
  ref,
) {
  return (
    <TableContext.Provider value={{ dense }}>
      <table
        {...rest}
        ref={ref}
        className={clsx(
          styles.table,
          dense && styles.dense,
          stickyHead && styles.stickyHead,
          className,
        )}
      >
        <caption className={clsx(styles.caption, hideCaption && styles.captionHidden)}>
          {caption}
        </caption>
        {children}
      </table>
    </TableContext.Provider>
  );
});

export const TableHead = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TableHead({ className, children, ...rest }, ref) {
    return (
      <thead {...rest} ref={ref} className={clsx(styles.thead, className)}>
        {children}
      </thead>
    );
  },
);

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  function TableBody({ className, children, ...rest }, ref) {
    return (
      <tbody {...rest} ref={ref} className={clsx(styles.tbody, className)}>
        {children}
      </tbody>
    );
  },
);

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
  /** Hover affordance for a row that drills down. The control is inside a cell. */
  interactive?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { selected = false, interactive = false, className, children, ...rest },
  ref,
) {
  return (
    <tr
      {...rest}
      ref={ref}
      aria-selected={selected ? true : undefined}
      className={clsx(styles.tr, interactive && styles.interactive, selected && styles.selected, className)}
    >
      {children}
    </tr>
  );
});

// `align` is logical here (start/end respect writing direction); the deprecated
// HTML presentational attribute of the same name is dropped.
export interface TableHeaderCellProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /** Column width preset from the token set. `flex` leaves it to the layout. */
  width?: ColumnPreset;
  align?: 'start' | 'end' | 'center';
  /** Renders the sort control and reports `aria-sort`. */
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSortChange?: (next: SortDirection) => void;
  /** Announced with the sort control, e.g. "Sort by stage". */
  sortLabel?: string;
  children: ReactNode;
}

const NEXT: Record<SortDirection, SortDirection> = { none: 'asc', asc: 'desc', desc: 'none' };
const ARIA_SORT = { asc: 'ascending', desc: 'descending', none: 'none' } as const;

export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  function TableHeaderCell(
    {
      width,
      align = 'start',
      sortable = false,
      sortDirection = 'none',
      onSortChange,
      sortLabel,
      className,
      style,
      children,
      ...rest
    },
    ref,
  ) {
    const widthVar = width === undefined ? undefined : columnToken(width);
    const vars: StyleWithVars = {};
    if (widthVar !== undefined) vars['--dpem-col-w'] = widthVar;

    const SortGlyph =
      sortDirection === 'asc' ? SortAscIcon : sortDirection === 'desc' ? SortDescIcon : SortIcon;

    return (
      <th
        {...rest}
        ref={ref}
        scope={rest.scope ?? 'col'}
        aria-sort={sortable ? ARIA_SORT[sortDirection] : undefined}
        className={clsx(
          styles.th,
          styles[`align-${align}`],
          widthVar !== undefined && styles.sized,
          className,
        )}
        style={mergeStyle(vars, style)}
      >
        {sortable ? (
          <button
            type="button"
            className={styles.sortButton}
            aria-label={sortLabel}
            onClick={() => onSortChange?.(NEXT[sortDirection])}
          >
            <span className={styles.thLabel}>{children}</span>
            <SortGlyph className={styles.sortGlyph} />
          </button>
        ) : (
          <span className={styles.thLabel}>{children}</span>
        )}
      </th>
    );
  },
);

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  align?: 'start' | 'end' | 'center';
  /** Mono, for canonical ids and numbers that must line up. */
  monospace?: boolean;
  /** Clip to one line with an ellipsis instead of wrapping. */
  truncate?: boolean;
  children: ReactNode;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { align = 'start', monospace = false, truncate = false, className, children, ...rest },
  ref,
) {
  const { dense } = useContext(TableContext);
  return (
    <td
      {...rest}
      ref={ref}
      className={clsx(
        styles.td,
        styles[`align-${align}`],
        monospace && styles.mono,
        truncate && styles.truncate,
        dense && styles.tdDense,
        className,
      )}
    >
      {children}
    </td>
  );
});
